import React, { useEffect, useRef } from "react";
import { loadGoogleMaps } from "./googleapi";

// Centro por defecto (CDMX) cuando el desarrollo aún no tiene coordenadas
// válidas. Sirve para que el usuario pueda arrastrar el pin desde un punto
// razonable en vez de aparecer en (0,0) en medio del océano.
const DEFAULT_CENTER = { lat: 19.4326, lng: -99.1332 };

const isValidCoord = (lat, lng) =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  !Number.isNaN(lat) &&
  !Number.isNaN(lng) &&
  !(lat === 0 && lng === 0) &&
  lat >= -90 &&
  lat <= 90 &&
  lng >= -180 &&
  lng <= 180;

// Mapa con pin arrastrable. Complementa al buscador de Google: si la dirección
// se geocodifica bien, el pin se coloca solo; si Google no encuentra la
// dirección exacta, el usuario puede arrastrar el pin (o hacer clic en el mapa)
// para fijar la ubicación correcta. Cada cambio del pin reporta lat/lng.
const LocationMap = ({ apiKey, lat, lng, onChange, disabled }) => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // Mantén el callback actualizado sin re-inicializar el mapa.
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Inicializa el mapa y el marcador una sola vez.
  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps(apiKey)
      .then((google) => {
        if (cancelled || !mapRef.current || mapInstance.current) return;

        const hasCoord = isValidCoord(lat, lng);
        const center = hasCoord ? { lat, lng } : DEFAULT_CENTER;

        const map = new google.maps.Map(mapRef.current, {
          center,
          zoom: hasCoord ? 16 : 12,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
        });
        mapInstance.current = map;

        const marker = new google.maps.Marker({
          position: center,
          map,
          draggable: !disabled,
        });
        markerRef.current = marker;

        const report = (position) => {
          if (position && onChangeRef.current) {
            onChangeRef.current({ lat: position.lat(), lng: position.lng() });
          }
        };

        marker.addListener("dragend", () => report(marker.getPosition()));
        // Clic en el mapa mueve el pin: alternativa cómoda al arrastre.
        if (!disabled) {
          map.addListener("click", (e) => {
            marker.setPosition(e.latLng);
            report(e.latLng);
          });
        }
      })
      .catch((err) => console.error("[GoogleMaps][Map]", err.message));

    return () => {
      cancelled = true;
    };
    // Solo depende de apiKey: el mapa se crea una vez y luego se sincroniza en
    // los efectos de abajo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey]);

  // Sincroniza el pin/centro cuando las coordenadas cambian desde fuera
  // (p. ej. al elegir una dirección en el buscador de Google).
  useEffect(() => {
    if (!mapInstance.current || !markerRef.current) return;
    if (!isValidCoord(lat, lng)) return;

    const current = markerRef.current.getPosition();
    // Evita recentrar en bucle cuando el cambio vino del propio arrastre.
    if (
      current &&
      Math.abs(current.lat() - lat) < 1e-7 &&
      Math.abs(current.lng() - lng) < 1e-7
    ) {
      return;
    }

    const pos = { lat, lng };
    markerRef.current.setPosition(pos);
    mapInstance.current.setCenter(pos);
    if (mapInstance.current.getZoom() < 15) mapInstance.current.setZoom(16);
  }, [lat, lng]);

  // Habilita/deshabilita el arrastre si cambia `disabled`.
  useEffect(() => {
    if (markerRef.current) markerRef.current.setDraggable(!disabled);
  }, [disabled]);

  return (
    <div className="flex flex-col gap-1">
      <div
        ref={mapRef}
        className="w-full h-[320px] rounded-[6px] border border-nemu-plata overflow-hidden"
      />
      {!disabled && (
        <span className="text-[13px] text-gray-500">
          Arrastra el pin o haz clic en el mapa para ajustar la ubicación exacta.
        </span>
      )}
    </div>
  );
};

export default LocationMap;
